import React from 'react'
import {
  Meter,
  PeriodBar,
  LegPip,
  SubjectAvatar,
  subjectFor,
  winningNow,
  headingFor,
  groupByGame,
  SelectionText,
  marginLead,
  toneFor,
  useChangePulse,
  fmt,
  TONE,
  MUTED
} from './LiveBetMeter'

// ============================================================================
// Live bet meter, phone edition — the panel inside a pending bet's card.
//
// DISPLAY ONLY, exactly as the desktop panel is. Everything drawn here comes
// from liveProgress() / liveLegs(); nothing in this file may influence a bet's
// status. settleBet / settleMulti own that.
//
// The PARTS are imported, not copied: the two meters, the pips, the period bar,
// the images and winningNow() are the desktop component's, so a phone can never
// draw a different verdict from a desktop looking at the same bet. What this
// file owns is the ARRANGEMENT.
//
// Desktop is a table row and lays out horizontally around a fixed 313px reserve
// (METER_INSET) that keeps every track starting at the same x. A card has no
// column to align to and about 310px of usable width in total, so that reserve
// is meaningless here and deliberately not ported. The card stacks instead:
// game context on one line, the meter full width beneath it. The track ends up
// WIDER than the desktop one, which is the one thing a phone has going for it.
// ============================================================================

// Recessed a step below the bet card it sits in (card #0e1626), the same
// relationship the desktop panel has to its row. Not a lighter slab: a live
// panel that jumps forward makes every card with one look like the selected
// one.
const PANEL = {
  marginTop: '10px',
  padding: '10px 11px 11px',
  borderRadius: '12px',
  backgroundColor: '#0b1322',
  border: '1px solid #1b2a45'
}

const HEADING = {
  fontSize: '12px',
  fontWeight: 700,
  color: '#94a3b8',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  letterSpacing: '0.02em'
}

const AWAITING = {
  fontSize: '10.5px',
  color: MUTED,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap'
}

/**
 * Where the game is up to: the period label, then the bar.
 *
 * Desktop runs the same information across a single line above the meter
 * (`GameHeader`). The phone stacks the bar under the label instead of sitting it
 * alongside, since a 104px bar and a heading do not share 300px with the
 * awaiting-settlement note.
 *
 * "Awaiting settlement" rides the same line as the label rather than sitting
 * under the bar: the gap between a bet being decided and being settled is up to
 * an hour, and a pending card with no explanation reads as a bug.
 */
function GameLine({ live }) {
  const { segments, period, completedPeriods, periodFraction, decided } = live
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px' }}>
        <span style={HEADING}>{headingFor(live)}</span>
        {decided && <span style={AWAITING}>Awaiting settlement</span>}
      </div>

      <PeriodBar
        segments={segments}
        period={period}
        completedPeriods={completedPeriods}
        periodFraction={periodFraction}
        marginTop="6px"
      />
    </div>
  )
}

/**
 * Margin markets (head to head, handicaps, alternate lines) as TEXT, which is
 * the one meter the phone does not draw.
 *
 * Desktop fills a bar out from a centre that is the line, with a second tick for
 * DRAW and both team names labelling the axis. That is four things reading off
 * one 300px track, and the axis is the part that breaks first: two full names do
 * not fit, and shortening them to nicknames leaves the whole apparatus resting
 * on a label the user has to decode. The readout underneath was always the
 * sentence the bar was drawing, so on a phone it is kept and the bar is dropped.
 *
 * Nothing is lost that a phone could have read anyway:
 *   who leads, by how much  -> the text
 *   covering or not          -> the COLOUR, straight off winningNow via toneFor,
 *                               so it agrees with the bar a desktop would draw
 *   what it has to beat      -> the line, beside it
 *
 * Progress markets (props, totals) keep their bar. That one is a value climbing
 * toward a target at the end of the track, which survives a narrow card intact.
 */
function MarginReadout({ live }) {
  const { text, full } = marginLead(live)
  const tone = toneFor(live)
  const hasLine = live.line != null

  // Only a winning bet glows, the same rule the bars follow.
  const lit = tone.ring

  // Motion conveys a data change and nothing else: the number lifts when the
  // score ticks, exactly as the chip on a bar does.
  const flash = useChangePulse(live.current, 400)

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', marginTop: '10px' }}>
      <span
        className={flash ? 'bc-lm-flash' : undefined}
        title={full}
        style={{
          fontSize: '13.5px',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: tone.main,
          textShadow: lit ? `0 0 9px ${tone.ring}` : 'none',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </span>

      {/* What that margin has to beat. Head to head carries no point, and there
          the bet is simply the game, so there is nothing to print. */}
      {hasLine && (
        <span style={{ fontSize: '11px', color: MUTED, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {`line ${live.line > 0 ? '+' : ''}${fmt(live.line)}`}
        </span>
      )}
    </div>
  )
}

/**
 * Whichever of the two a bet gets. Split here rather than inside Meter so the
 * desktop component keeps one shape per market and the phone's departure is
 * visible in one place.
 */
function BetMeter({ live }) {
  return live.meter === 'margin'
    ? <MarginReadout live={live} />
    : <Meter live={live} compact />
}

/**
 * One pending single whose game is in play. `live` is liveProgress()'s return,
 * `bet` the row it came from, and this renders nothing when live was null.
 *
 * No toggle, same as desktop: a live bet is the thing you opened the tracker to
 * look at, and the panel is three short lines, so several cards carrying one
 * still scroll sensibly.
 */
export default function LiveBetCard({ live, bet }) {
  if (!live) return null

  // liveProgress returns display fields rather than the bet, so rebuild the
  // shape subjectFor expects. Legs pass their record straight in.
  const subject = subjectFor({
    sport:       live.sport,
    player_name: live.playerName,
    team_name:   live.backedTeam,
    home_team:   live.teams?.home?.team,
    away_team:   live.teams?.away?.team
  })

  return (
    <div style={PANEL}>
      {/* Selection first, then where the game is up to, then the meter. A leg
          reads in that order too, so the two are the same object at different
          depths rather than two different designs.

          The image is skipped when there is none rather than holding an empty
          slot: a card has one meter, so there is no column here to keep
          straight, and an empty circle would just be clutter. */}
      <div className="bc-lmm-sub" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {subject && <SubjectAvatar subject={subject} />}
        <SelectionText unit={bet} fallback={live.playerName || live.backedTeam || 'Selection'} compact />
      </div>

      <div style={{ display: 'flex', marginTop: '10px', paddingTop: '9px', borderTop: '1px solid #16233c' }}>
        <GameLine live={live} />
      </div>

      <BetMeter live={live} />
    </div>
  )
}

/**
 * A multi's legs. `legs` is liveLegs()'s return, `open` and `onToggle` are the
 * parent's Set of open bet ids.
 *
 * COLLAPSED by default, as on desktop, and for the same reason with more force:
 * a five-leg multi is five meters plus a header per fixture, which on a phone is
 * several screens of scroll wedged into one card.
 */
export function LiveMultiCard({ legs, open = false, onToggle }) {
  if (!legs || legs.length === 0) return null

  // A multi is lost the moment ANY leg loses, so a lost leg is the headline
  // whatever the rest are doing.
  const lostLeg = legs.some(l => l.state === 'decided' && winningNow(l.live) === false)

  // The game to name while collapsed: the one actually in play. Legs sit in
  // different games on different days, so naming any other would point at a
  // fixture nothing is happening in.
  const liveEntry = legs.find(l => l.state === 'live' && l.live) || legs.find(l => l.live)
  const anyLegLive = legs.some(l => l.state === 'live')

  return (
    <div style={PANEL}>
      <button
        type="button"
        className={`bc-lm-toggle bc-lmm-toggle${!open && anyLegLive ? ' bc-lm-toggle--live' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          cursor: onToggle ? 'pointer' : 'default',
          marginBottom: open ? '10px' : 0
        }}
      >
        {/* Desktop fits pips, state, fixture, period bar and the pill on ONE
            line. At 310px that line has to break, so it breaks where the
            meaning does: what the legs are doing on top, where the game is up
            to underneath. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* The pips carry every leg's state in leg ORDER, which is what makes
              collapsing a fair trade rather than a stub: the third pip is the
              third leg listed once it opens. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {legs.map((leg, i) => (
              <LegPip
                key={i}
                state={leg.state}
                winning={winningNow(leg.live)}
                label={leg.leg?.selection || `Leg ${i + 1}`}
              />
            ))}
          </div>

          {/* A verb, not a count. The pips already say how many legs there are
              and what each is doing. */}
          <span
            className="bc-lm-toggle-cta"
            style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: 'auto', fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.07em', whiteSpace: 'nowrap' }}
          >
            {open ? 'Hide legs' : 'View legs'}
            <svg
              width="9" height="9" viewBox="0 0 12 12" aria-hidden="true"
              style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 200ms ease' }}
            >
              <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>

        {lostLeg && (
          <div style={{ marginTop: '7px', fontSize: '11px', fontWeight: 700, color: TONE.lost.main }}>
            a leg has gone down
          </div>
        )}

        {/* Where the multi is up to while the legs are hidden. Dropped once
            open, since every group below prints its own header.

            The period bar is the reason no LIVE badge is needed here: expanded,
            the meters filling ARE the liveness signal, but collapsing hides
            them, and a strip of static pips gives no sign the thing is running.
            A bar creeping across its segment says it with data. */}
        {!open && liveEntry && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginTop: '7px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#cbd5e1', whiteSpace: 'nowrap' }}>
              {headingFor(liveEntry.live)}
            </span>

            {liveEntry.gameLabel && (
              <span style={{ fontSize: '11px', fontWeight: 500, color: '#94a3b8', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {liveEntry.gameLabel}
              </span>
            )}

            {liveEntry.live.segments && (
              <div style={{ width: '58px', flex: '0 0 auto', marginLeft: 'auto' }}>
                <PeriodBar
                  segments={liveEntry.live.segments}
                  period={liveEntry.live.period}
                  completedPeriods={liveEntry.live.completedPeriods}
                  periodFraction={liveEntry.live.periodFraction}
                  marginTop={0}
                />
              </div>
            )}
          </div>
        )}
      </button>

      {open && groupByGame(legs).map((group, gi) => {
        // Every leg of a game shares that game's state, so the header comes off
        // the first leg that has any. A same-game multi therefore shows one
        // header rather than repeating the identical period bar per leg.
        const anyLive = group.items.find(item => item.live)?.live ?? null

        // Whether this fixture's legs need an image gutter at all. Nearly always
        // true now that team markets have crests, but a sport with no folders
        // yet gets a clean layout instead of an empty column.
        const groupHasImages = group.items.some(item => subjectFor(item.leg))

        return (
          <div
            key={group.key}
            style={{
              paddingTop: gi === 0 ? 0 : '11px',
              marginTop: gi === 0 ? 0 : '11px',
              borderTop: gi === 0 ? 'none' : '1px solid #1b2a45'
            }}
          >
            {/* The fixture on its own line, with the period bar under it.
                Desktop runs the two side by side; at this width the fixture
                alone is most of the line. */}
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {group.items[0]?.gameLabel || `Game ${gi + 1}`}
            </div>

            {anyLive ? (
              <div style={{ marginTop: '5px' }}>
                <GameLine live={anyLive} />
              </div>
            ) : (
              // No fixture matched, or it has not started. Say so rather than
              // leaving a gap the eye reads as a rendering fault.
              <div style={{ ...AWAITING, marginTop: '5px' }}>
                {group.items[0]?.started ? 'No live data' : 'Not started'}
              </div>
            )}

            {group.items.map((entry, i) => (
              <div
                key={i}
                style={{
                  marginTop: '11px',
                  paddingTop: '10px',
                  borderTop: '1px solid #16233c'
                }}
              >
                <div className="bc-lmm-sub" style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                  {/* Reserved for the whole GROUP, not per leg, so a group
                      mixing a player prop with a team market keeps one straight
                      edge down the panel. */}
                  {groupHasImages && <SubjectAvatar subject={subjectFor(entry.leg)} />}

                  {/* Selection over market, the same two-line shape the multi
                      builders use, so a leg reads the same wherever the user
                      meets it. The market matters more here than on a single:
                      the card's own Market row says "Multi", so this is the only
                      place a head-to-head leg can be told from a handicap. */}
                  <SelectionText unit={entry.leg} fallback={`Leg ${i + 1}`} compact />
                </div>

                {/* BENEATH the name, not beside it, which is the one place this
                    layout has to depart from desktop's. Desktop puts them side
                    by side so the tracks line up in a column; splitting 310px
                    between a selection and a track would leave both unreadable,
                    and the divider above each leg is what keeps the bar attached
                    to its own name. */}
                {entry.live && <BetMeter live={entry.live} />}
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}
